import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import Header from "@components/elements/Header";
import { QuestionnaireProgress } from "@components/elements/QuestionnaireProgress";
import Questionnaire, { OnNextPayload } from "@components/Questionnaire";
import { useAuth } from "@contexts/auth/AuthProvider";
import useCase from "@hooks/case/useCase";
import useQuestionnaireIndex from "@hooks/questionnaire/useQuestionnaireIndex";
import { useQuestionnaireProgress } from "@hooks/questionnaire/useQuestionnaireProgress";
import { useParentalConsentMonitoring } from "@hooks/questionnaire/useParentalConsentMonitoring";
import { useQuestionValidation } from "@hooks/questionnaire/useQuestionValidation";
import useAddons from "@hooks/recommendations/useAddons";
import useRegionBased from "@hooks/recommendations/useRegionBased";
import useUser from "@hooks/user/useUser";
import * as QuestionnaireSlice from "@store/questionnaire/questionnaireSlice";
import { Case, RecommendedConditions } from "@types-runway/case";
import { Condition, ConditionTypes } from "@types-runway/condition";
import {
  AnswerType,
  EntityType,
  NoticeMessage,
  Page,
  Question,
  QuestionnairePages,
  Questions,
  StorageMappings,
  VALID_QUESTIONNAIRE_LENGTH,
} from "@types-runway/questionnaire";
import { Partner, RootState, safeParse } from "@types-runway/state";
import { Gender, GenderType, User } from "@types-runway/user";
import { ANALYTICS_EVENTS, reportCustomEvent } from "@utils/analytics/events";
import { flattenQuestionnairePages } from "@utils/questionnaire/pageFlattening";
import { resolveEntityUpdate } from "@utils/questionnaire/storageMappings";
import { resolveTripForAddons } from "@utils/cart/resolveTripForAddons";
import { uniqueConditions } from "@utils/case/uniqueConditions";
import questionnaireContent from "@utils/questionnaire/questionsPages.json";

type QuestionnairePageProps = {
  startQuestionnaire: React.Dispatch<React.SetStateAction<boolean>>;
  partner: Partner | null;
  visitFeeDiscount?: number;
};

const QuestionnairePage: React.FC<QuestionnairePageProps> = ({
  startQuestionnaire,
  partner,
  visitFeeDiscount,
}) => {
  // React Hooks
  const dispatch = useDispatch();
  const history = useHistory();

  // Custom hooks
  const { getQuestionDependencyMatch, questionIsAlreadyAsked } = useQuestionValidation();
  const { reportUnresolvableDob } = useParentalConsentMonitoring();
  const { updateCase } = useCase();
  const { getAddons } = useAddons();
  const { updateUser } = useUser();
  const { getRegionBased } = useRegionBased();
  const { isAuth } = useAuth();
  const { writeIndex, readIndex } = useQuestionnaireIndex();

  // Global state
  const selectedConditions = useSelector<RootState, Condition[]>(
    (state) => state.case.newCase.data.conditions
  );
  const questions = useSelector<RootState, Questions>((state) => state.questions);
  const newCase = useSelector<RootState, Case>((state) => state.case.newCase.data);
  const user = useSelector<RootState, User>((state) => state.user.data);

  // Local state
  const [selectedStateKey, setSelectedStateKey] = useState<string>("");
  const [bannerContent, setBannerContent] = useState<NoticeMessage | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [addonRecommendations, setAddonRecommendations] = useState<RecommendedConditions | null>(
    null
  );
  const [regionRecommendations, setRegionRecommendations] = useState<RecommendedConditions | null>(
    null
  );
  const [pageIsLoading, setPageIsLoading] = useState<boolean>(false);
  const [isRegionLoading, setIsRegionLoading] = useState<boolean>(false);
  const [isPlusFlow, setIsPlusFlow] = useState<boolean>(
    sessionStorage.getItem("flow") === "plus" || false
  );
  const flowType = isPlusFlow ? "plus" : "standard";
  // Tracks the latest trip data across page navigations. Updated when
  // orchestrateCaseUpdates flushes, so loadDynamicPages always has fresh
  // trip data even before the async saga updates Redux.
  const latestTripRef = useRef(newCase.trip);

  const pendingUpdatesRef = useRef<{
    user?: Partial<User>;
    case?: Partial<Case>;
    questions?: Questions;
  }>({
    user: {},
    case: {},
    questions: {},
  });
  // Tracks the last-viewed step index so the step_view effect can tell a
  // forward advance from a back-navigation and skip re-reporting the latter.
  const lastViewedIndexRef = useRef<number | null>(null);
  // Guards goToNextPage against a double-fired step_complete event when
  // handleNextButtonClick is invoked twice in quick succession (e.g. a
  // double click) while the first call is still preparing its transition.
  // It is released before the page transition itself: committing the next
  // page synchronously re-enters handleNextButtonClick whenever that page's
  // dependsOn rule skips it, and that re-entry must not be dropped.
  const isAdvancingRef = useRef(false);

  // Helper to stage updates
  const stageEntityUpdate = (
    entityType: "user" | "case" | "questions",
    partial: Partial<User> | Partial<Case> | Questions
  ) => {
    const prev = pendingUpdatesRef.current[entityType as "user" | "case" | "questions"] ?? {};
    const merged: Record<string, unknown> = { ...prev };

    for (const [key, value] of Object.entries(partial)) {
      const prevValue = (prev as Record<string, unknown>)[key];
      const bothAreObjects =
        prevValue && typeof prevValue === "object" && !Array.isArray(prevValue) &&
        value && typeof value === "object" && !Array.isArray(value);

      merged[key] = bothAreObjects
        ? { ...(prevValue as Record<string, unknown>), ...(value as Record<string, unknown>) }
        : value;
    }

    pendingUpdatesRef.current[entityType as "user" | "case" | "questions"] =
      merged as (Partial<User> & Partial<Case> & Questions) | undefined;
  };

  const goToNextPage = (stepsToAdvance = 1) => {
    // A screen the questionnaire skips advances through here too; only a
    // screen the patient was asked counts as completed.
    if (currentPageInfo.currentPage && isCurrentScreenAsked) {
      reportCustomEvent(ANALYTICS_EVENTS.QUESTIONNAIRE_STEP_COMPLETE, {
        step_index: currentPageInfo.currentIndex,
        // Raw position/length in flattenedPages — includes dependsOn/automatic
        // pages the patient never sees. questions_left is the asked count.
        step_count: flattenedPages.length,
        step_category: currentPageInfo.currentPage.section,
        flow_type: flowType,
        ...progressLeftProperties,
      });
    }

    // Clear banner on next
    setBannerContent(null);
    const nextIndex = currentPageInfo.currentIndex + stepsToAdvance;

    if (nextIndex > flattenedPages.length) {
      return;
    }

    // If next index equals questionnaire length, we've reached the end of the questionnaire.
    if (nextIndex === flattenedPages.length) {
      const isAuthenticated = localStorage.getItem("token");

      if (isAuthenticated) {
        history.push("/checkout");
      } else {
        history.push("/registration");
      }
      return;
    }

    // Navigate to next page
    if (nextIndex < flattenedPages.length) {
      // Update question progress in session.
      if (flattenedPages[nextIndex].inputType.input !== "automatic") {
        writeIndex(nextIndex);
      }
      // Set selected key;
      setSelectedStateKey(flattenedPages[nextIndex].stateKey);
    }
  };

  const goToPreviousPage = () => {
    const isCurrentPageAfterUpsell =
      flattenedPages[currentPageInfo.currentIndex - 1]?.stateKey === "addOns";
    const stepsToMoveBack = isCurrentPageAfterUpsell ? 2 : 1;
    const prevIndex = currentPageInfo.currentIndex - stepsToMoveBack;

    // Clear banner on back, so a notice raised on this page never leaks
    // onto the previous one.
    setBannerContent(null);

    if (prevIndex >= 0) {
      // Update question progress in session.
      writeIndex(prevIndex);
      // Set selected key.
      setSelectedStateKey(flattenedPages[prevIndex].stateKey);
    } else {
      // Reset index.
      writeIndex(0);
      startQuestionnaire(false);
    }
  };

  const updateQuestionnaire = (question: Question, stateKey?: string) => {
    // First update the questionnaire state if needed
    if (currentPageInfo.currentPage?.stateKey) {
      stageEntityUpdate("questions", {
        [stateKey ?? currentPageInfo.currentPage.stateKey]: question,
      });

      dispatch(
        QuestionnaireSlice.updateQuestion({
          stateKey: stateKey ?? currentPageInfo.currentPage.stateKey,
          question,
        })
      );
    }
  };

  const mapAnswer = (storageMappings: StorageMappings<EntityType>, answer: AnswerType) => {
    const { nestingProperty } = storageMappings;
    const existingNestedState = nestingProperty
      ? (newCase[nestingProperty as keyof Case] as Record<string, unknown>) ?? undefined
      : undefined;

    const result = resolveEntityUpdate(storageMappings, answer, existingNestedState);
    if (result) {
      stageEntityUpdate(result.entityType, result.update);
    }
  };

  const orientUserWithPlusFlow = (active: boolean) => {
    sessionStorage.setItem("isPlus", active.toString());
    setIsPlusFlow(active);
  };

  const shouldRender = (nextPage: Page, questionsOverride?: Questions) => {
    if (!nextPage) return false;

    /**
     * Business decision: orient the user with the plus flow as the user
     * at this stage still has an empty cart. Evaluated on the allergies page,
     * the first always-rendered page after the add-ons upsell.
     */
    const lastCartPresentationHasPassed = currentPageInfo.currentPage?.stateKey === "allergies";
    const isEmptyCart = selectedConditions.length === 0;

    if (lastCartPresentationHasPassed) {
      orientUserWithPlusFlow(isEmptyCart);
    }

    if (nextPage.inputType.input === "automatic") {
      return false;
    }

    if (!nextPage.dependsOn && !nextPage.storageMappings) return true;

    if (nextPage.dependsOn) {
      const context = {
        questions: questionsOverride ?? questions,
        isAuth,
        partner,
        query: new URLSearchParams(window.location.search),
      };
      reportUnresolvableDob(nextPage.dependsOn, context);
      return getQuestionDependencyMatch(nextPage.dependsOn, context);
    }

    // If authenticated, we verify storageMappings
    if (isAuth) {
      if (nextPage.storageMappings) {
        return !questionIsAlreadyAsked(nextPage.storageMappings.mappings);
      }
    }

    return true;
  };

  const loadDynamicPages = async (
    stepsToAdvance = 1,
    effectiveQuestions: Questions = questions
  ) => {
    const nextDynamicPage = flattenedPages[currentPageInfo.currentIndex + stepsToAdvance];

    // Background fetch region-based recommendations.
    if (nextDynamicPage?.stateKey === "tripDescription") {
      // If we should render it, we load the dynamic page.
      if (shouldRender(nextDynamicPage, effectiveQuestions)) {
        const destination = effectiveQuestions["travelDestination"]?.answer?.toString();
        // Fetch in background and jump to next page.
        setIsRegionLoading(true);
        // Clear region recommendations before fetching new ones.
        setRegionRecommendations(null);
        // Fetch region-based recommendations.s
        getRegionBased(destination).then((regionBased) => {
          setRegionRecommendations(regionBased);
          setIsRegionLoading(false);
        });
      }
      return stepsToAdvance;
    }

    // Pre-load dynamic page questions data.
    // Load addons
    if (nextDynamicPage?.stateKey === "addOns") {
      // If we should render it, we load the dynamic page.
      if (shouldRender(nextDynamicPage, effectiveQuestions)) {
        setPageIsLoading(true);
        const addons = await getAddons(
          user.gender ?? Gender.Other,
          newCase.conditions.map((condition) => condition.product.name as ConditionTypes),
          effectiveQuestions["travelDestination"]?.answer?.toString(),
          latestTripRef.current?.from
        );
        setPageIsLoading(false);
        if (addons.conditions.length === 0) {
          setAddonRecommendations({ conditions: [], reason: "" });
          // Business decision: skip the page by advancing 2 steps
          stepsToAdvance++;
        } else {
          // Business decision: show the page by advancing 1 step
          setAddonRecommendations(addons);
        }
      }
      return stepsToAdvance;
    }

    // End of dynamic page loading
    return stepsToAdvance;
  };

  // React Hooks
  const flattenedPages = useMemo(() => {
    return flattenQuestionnairePages(
      questionnaireContent as QuestionnairePages,
      selectedConditions,
      isPlusFlow
    );
  }, [selectedConditions, isPlusFlow]);

  // Find the current page and its index information
  const currentPageInfo = useMemo(() => {
    const currentPage = flattenedPages.find((page) => page.stateKey === selectedStateKey);
    const currentIndex = flattenedPages.findIndex((page) => page.stateKey === selectedStateKey);
    return { currentPage, currentIndex };
  }, [selectedStateKey, flattenedPages]);

  // Report questionnaire funnel progress on each forward step change.
  // Keyed on selectedStateKey (page identity), not currentPageInfo.currentIndex
  // (array position) — flattenedPages reindexes whenever selectedConditions or
  // isPlusFlow changes (e.g. accepting an addOns upsell), which shifts every
  // later page's position without the user having navigated anywhere.
  useEffect(() => {
    if (!currentPageInfo.currentPage) return;

    const isBackNavigation =
      lastViewedIndexRef.current !== null &&
      currentPageInfo.currentIndex < lastViewedIndexRef.current;
    lastViewedIndexRef.current = currentPageInfo.currentIndex;

    // A skipped screen is selected for one render before the questionnaire
    // moves past it; the patient never sees it.
    if (isBackNavigation || !isCurrentScreenAsked) return;

    reportCustomEvent(ANALYTICS_EVENTS.QUESTIONNAIRE_STEP_VIEW, {
      step_index: currentPageInfo.currentIndex,
      // Raw position/length in flattenedPages — includes dependsOn/automatic
      // pages the patient never sees. questions_left is the asked count.
      step_count: flattenedPages.length,
      step_category: currentPageInfo.currentPage.section,
      flow_type: flowType,
      ...progressLeftProperties,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStateKey]);

  // Initialize the current index.
  useMemo(() => {
    let index = readIndex() ?? 0;

    if (!Boolean(index) && newCase.case_questions?.length >= VALID_QUESTIONNAIRE_LENGTH) {
      // Set index to last question if index is not set and there is at least 15 questions answered.
      index = flattenedPages.length - 1;
    }

    if (index !== currentPageInfo.currentIndex && index < flattenedPages.length) {
      setSelectedStateKey(flattenedPages[index].stateKey);
    }

    setCurrentIndex(index);
  }, []);

  // Sync path when flattenedPages changes
  useEffect(() => {
    // If we don't have any pages yet, we can't do anything.
    if (flattenedPages.length === 0) {
      return;
    }

    // The path is basically the selected state key.
    const requiresPathSync = !selectedStateKey;

    if (requiresPathSync) {
      const syncsFromCurrentSession =
        currentIndex &&
        currentIndex !== currentPageInfo.currentIndex &&
        currentIndex < flattenedPages.length;

      const syncsFromStoredSession = newCase.case_questions?.length >= VALID_QUESTIONNAIRE_LENGTH;

      if (syncsFromCurrentSession) {
        setSelectedStateKey(flattenedPages[currentIndex].stateKey);
        return;
      }

      if (syncsFromStoredSession) {
        const automaticPagesCount = flattenedPages.filter(
          (page) => page.inputType && page.inputType.input === "automatic"
        ).length;
        const lastValidIndex = flattenedPages.length - automaticPagesCount;
        const storedIndex = readIndex();
        const validIndex =
          storedIndex && storedIndex < flattenedPages.length ? storedIndex : lastValidIndex;

        setSelectedStateKey(flattenedPages[validIndex].stateKey);
        return;
      }

      // Otherwise, we initialize the path to the first page.
      setSelectedStateKey(flattenedPages[0].stateKey);
    }
  }, [flattenedPages]);

  const {
    segments,
    timeLeftLabel,
    shownMinutesLeft,
    questionsLeft,
    isCurrentScreenAsked,
  } = useQuestionnaireProgress({
    flattenedPages,
    selectedStateKey,
    partner,
    pageIsLoading,
    addonRecommendations,
  });
  // The time estimate for the current screen, sent with the step
  // events so drop-off can be read against the length the patient perceived.
  const progressLeftProperties = {
    questions_left: questionsLeft,
    estimated_minutes_left: shownMinutesLeft,
    time_left_label: timeLeftLabel,
  };

  const syncQuestionUpdates = (stagedQuestionsUpdate: Questions): Questions => {
    // Build an effective questions snapshot that includes the most recent answer from payload
    return stagedQuestionsUpdate ? { ...questions, ...stagedQuestionsUpdate } : questions;
  };

  const orchestrateCaseUpdates = (
    stagedCaseUpdate: Partial<Case>,
    effectiveQuestions: Questions
  ) => {
    const caseUpdate = stagedCaseUpdate ? { ...newCase, ...stagedCaseUpdate } : newCase;

    // Update only the fields that are usually updated in the questionnaire.
    updateCase(newCase.id ?? 0, isAuth, {
      conditions: caseUpdate.conditions,
      trip: caseUpdate.trip,
      questions: effectiveQuestions,
      totalQuestions: flattenedPages.length,
      UserId: user.id,
    });
  };

  const syncUserUpdates = (stagedUserUpdate: Partial<User>) => {
    if (!stagedUserUpdate || Object.keys(stagedUserUpdate).length === 0) return;
    updateUser(stagedUserUpdate, isAuth);
  };

  const handleNextButtonClick = async (payload?: OnNextPayload) => {
    // Ignore re-entrant calls (e.g. a double click) while the previous
    // invocation is still preparing its page transition.
    if (isAdvancingRef.current) return;
    isAdvancingRef.current = true;

    let stepsToAdvance: number;

    try {
      // Snapshot all staged updates before dispatching, so both dispatches
      // use consistent staged state rather than stale Redux state.
      const stagedUserUpdate = pendingUpdatesRef.current.user;
      const stagedQuestionsUpdate = pendingUpdatesRef.current.questions;
      const effectiveQuestions = syncQuestionUpdates(stagedQuestionsUpdate ?? questions);

      // Always update case if not requested to skip.
      if (!payload?.skipUpdate) {
        if (payload?.addonConditions) {
          stageEntityUpdate("case", {
            conditions: uniqueConditions([...newCase.conditions, ...payload.addonConditions]),
          });
        }

        const stagedCaseUpdate = pendingUpdatesRef.current.case;

        // Dispatch user update first so the saga picks up the latest user state.
        if (stagedUserUpdate) {
          syncUserUpdates(stagedUserUpdate);
        }

        // Resolve trip before clearing staged updates — loadDynamicPages
        // may need it across multiple page navigations (e.g., calendar → skip → addons).
        latestTripRef.current = resolveTripForAddons(stagedCaseUpdate, newCase);

        orchestrateCaseUpdates(stagedCaseUpdate ?? newCase, effectiveQuestions);

        // Clear staged updates after both dispatches have been issued.
        pendingUpdatesRef.current = {};
      } else {
        // Even when skipping case update, flush user updates.
        if (stagedUserUpdate) {
          syncUserUpdates(stagedUserUpdate);
        }
      }

      stepsToAdvance = await loadDynamicPages(1, effectiveQuestions);
    } finally {
      isAdvancingRef.current = false;
    }

    goToNextPage(stepsToAdvance);
  };

  // Render guard
  if (!currentPageInfo.currentPage) {
    return null;
  }

  return (
    <>
      <Header
        previous={goToPreviousPage}
progressIndicator={<QuestionnaireProgress segments={segments} />}
      />
      <Questionnaire
        currentPage={currentPageInfo.currentPage as Page}
        pageIndex={currentPageInfo.currentIndex}
        onNext={handleNextButtonClick}
        onPrev={goToPreviousPage}
        initialValue={questions[currentPageInfo.currentPage?.stateKey || ""]}
        onAnswer={(question, stateKey, skipQuestionnaireUpdate) => {
          if (!skipQuestionnaireUpdate) {
            updateQuestionnaire(question, stateKey);
          }

          // Only map for base questions; skip mapping for override-derived answers.
          if (!question.overriden && currentPageInfo.currentPage?.storageMappings) {
            mapAnswer(currentPageInfo.currentPage.storageMappings, question.answer);
          }
        }}
        bannerContent={bannerContent}
        setBannerContent={setBannerContent}
        partner={partner}
        visitFeeDiscount={visitFeeDiscount}
        addonRecommendations={addonRecommendations ?? { conditions: [], reason: "" }}
        regionRecommendations={regionRecommendations ?? { conditions: [], reason: "" }}
        checkRenderAllowed={shouldRender}
        pageIsLoading={pageIsLoading}
        isRegionLoading={isRegionLoading}
        isAuth={isAuth}
      />
    </>
  );
};

export default QuestionnairePage;
